// pages/HomePage.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Container,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Typography,
  Avatar,
  Card,
  CardContent,
  CardMedia,
} from "@mui/material";
import InfoCard from "../components/InfoCard";
import { useNavigate } from "react-router-dom";
import {
  formatCalendarDate,
  getNextEventPerSeasonGame,
  getUpcomingEvents,
} from "../utils/eventDate";
import { AC_EVO_FILES } from "../results/files.config";
import { buildAceStandings } from "../utils/aceResults";
import { getHomeMessages } from "../content/homeMessages";

/**
 * Home page (EXACT extraction from App.jsx)
 * Props: { data }
 */
export default function HomePage({ data }) {
  const navigate = useNavigate();
  const home = data?.home;
  const chips = home?.chips ?? data?.homeChips ?? ["Assetto Corsa", "Assetto Corsa Competizione"];
  const intro =
    home?.intro ??
    data?.homeIntro ??
    "Welcome to ARL – South Africa's ruthless late-apex league for Assetto Corsa Competizione & Assetto Corsa EVO. Clean racing, fierce pace, proper stewarding.";

  // Enhanced cards with placeholder images
  const cardTemplates = home?.cards ?? data?.homeCards ?? [
    {
      title: "Racing Excellence",
      body: "Professional grade sim racing with strict stewarding and competitive racing.",
      cta: { label: "Learn More", to: "/rules" },
    },
    {
      title: "Join The Grid",
      body: "Ready to compete? Join South Africa's premier sim racing league.",
      cta: { label: "Register", to: "/join" },
    },
    {
      title: "Championship",
      body: "Battle for glory across multiple seasons and championships.",
      cta: { label: "View Standings", to: "/standings" },
    },
  ];

  // Derive additional content from existing JSON
  const tz = data?.schedule?.timezone ?? data?.meta?.timezone ?? "Africa/Johannesburg";
  const events = Array.isArray(data?.schedule?.events) ? data.schedule.events : [];
  const seasons = Array.isArray(data?.seasons?.seasonsList) ? data.seasons.seasonsList : [];
  const schedule = Array.isArray(data?.schedule?.events) ? data.schedule.events : [];
  const standingsAC = data?.standings?.AC ?? {};
  const standingsACC = data?.standings?.ACC ?? {};
  const standingsACE = data?.standings?.AC_EVO ?? data?.standings?.ACEVO ?? {};
  const drivers = React.useMemo(
    () => (Array.isArray(data?.drivers?.list) && data.drivers.list.length > 0
      ? data.drivers.list
      : []),
    [data?.drivers?.list]
  );
  const teams = (Array.isArray(data?.teams?.teams) && data.teams.teams.length > 0
    ? data.teams.teams
    : []).slice(0, 3);
  const sponsors = Array.isArray(data?.sponsors) ? data.sponsors : [];
  const messages = getHomeMessages(
    typeof document === "undefined" ? undefined : document.documentElement.lang
  );
  const [aceSessions, setAceSessions] = React.useState([]);

  React.useEffect(() => {
    let active = true;

    Promise.all(
      AC_EVO_FILES.map(async (file) => {
        const url = process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/${file}` : `/${file}`;
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) throw new Error(`${file}: HTTP ${response.status}`);
        return response.json();
      })
    )
      .then((sessions) => {
        if (active) setAceSessions(sessions);
      })
      .catch(() => {
        if (active) setAceSessions([]);
      });

    return () => {
      active = false;
    };
  }, []);

  const aceComputed = React.useMemo(
    () => buildAceStandings(aceSessions, drivers, Number(standingsACE.dropRounds) || 0),
    [aceSessions, drivers, standingsACE.dropRounds]
  );
  const aceDrivers = aceComputed.drivers.length
    ? aceComputed.drivers
    : Array.isArray(standingsACE.drivers) ? standingsACE.drivers : [];
  const aceLeaders = [...aceDrivers]
    .sort((a, b) => (b.current_points ?? b.points ?? 0) - (a.current_points ?? a.points ?? 0))
    .slice(0, 5);
  const upcomingEvents = getUpcomingEvents(events, tz);
  const locale = typeof navigator === "undefined" ? "en-ZA" : navigator.language;
  const nextEventsBySeasonGame = getNextEventPerSeasonGame(events, tz);
  const cards = nextEventsBySeasonGame.map((event) => {
    const template = cardTemplates.find((card) => card?.body?.includes(event.game)) ?? {};
    const titlePrefix = template.title?.split(" Round")[0] || event.game;
    const startTime = event.practice || event.quali || event.race;
    const details = [
      event.game,
      event.type,
      formatCalendarDate(event.date, locale),
      startTime ? `${startTime} (${tz})` : null,
    ].filter(Boolean);

    return {
      image: template.image,
      title: `${titlePrefix} Round ${event.round} - ${event.track}`,
      body: details.join(" • "),
      cta: {
        label: template.cta?.label ?? "View Schedule",
        to: `/schedule?game=${encodeURIComponent(event.game)}`,
      },
    };
  });

  const getRounds = (s) => schedule.filter((s1) => {
    return s1?.season == s.season
  }).length ?? 0;

  return (
    <>
      <Box sx={{ position: "relative", overflow: "hidden", py: { xs: 2.5, sm: 4, md: 6 } }}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "url(team.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(.2) saturate(.75)",
            transform: "scale(1.04)",
          }}
        />
        <Container
          sx={{
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, sm: 4 },
          }}
        >
          <Stack spacing={3} alignItems="center" textAlign="center" sx={{ width: "100%" }}>
            <Box 
              component="iframe"
              src="https://www.youtube.com/embed/UlZWSsNDaHw?autoplay=1&playsinline=1"
              title="Abomination Racing League"
              allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{
                top: 0,
                left: 0,
                width: "min(100%, 960px)",
                aspectRatio: "16 / 9",
                height: "auto",
                border: 0,
                borderRadius: { xs: 1.5, md: 2.5 },
                boxShadow: "0 28px 90px rgba(0,0,0,.55)",
              }}
            />
          </Stack>
        </Container>
      </Box>

      {/* Epic Hero Section */}
      <Box sx={{ position: "relative", minHeight: { xs: 560, md: 680 }, overflow: "hidden" }}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: "url(gt3.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(.3) saturate(.85)",
            transform: "scale(1.03)",
          }}
        />
        <Container
          sx={{
            position: "relative",
            zIndex: 1,
            minHeight: { xs: 560, md: 680 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack spacing={{ xs: 2.5, md: 3 }} alignItems="center" textAlign="center" sx={{ py: 7 }}>
            <Typography
              variant="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "clamp(2.7rem, 13vw, 4.25rem)", md: "clamp(4.5rem, 7vw, 6.5rem)" },
                maxWidth: 1100,
                background: "linear-gradient(110deg, #f5f7fa 12%, #43d9e7 55%, #ff4d57 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 10px 50px rgba(0,0,0,.75)",
              }}
            >
              ABOMINATION RACING LEAGUE
            </Typography>
            <Typography variant="h5" sx={{ color: "#d6dce5", maxWidth: 760, fontSize: { xs: "1rem", sm: "1.2rem" }, lineHeight: 1.6 }}>
              {intro}
            </Typography>
            <Stack direction="row" flexWrap="wrap" justifyContent="center" sx={{ width: { xs: "100%", sm: "auto" } }}>
              {chips.map((c) => (
                <Chip key={c} label={c} color="primary" sx={{m: 1}}/>
              ))}
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: { xs: "100%", sm: "auto" }, pt: 1 }}>
              <Button fullWidth variant="contained" color="secondary" size="large" component={RouterLink} to="/join">
                Join The Grid
              </Button>
              <Button fullWidth variant="outlined" color="primary" size="large" component={RouterLink} to="/schedule">
                Race Schedule
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container sx={{ py: { xs: 3, sm: 5, md: 8 } }}>
        {/* Stats Section with Background */}
        <Box
          sx={{
            p: { xs: 2, sm: 4 },
            mb: { xs: 5, md: 8 },
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            backgroundImage: "url(1.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              insetInline: 0,
              bottom: 0,
              background: "rgba(0,12,32,0.85)",
            },
          }}
        >
          <Grid container spacing={{ xs: 1, sm: 3 }} sx={{ position: "relative", zIndex: 1, margin: "auto", width: "100%" }}>
            <Grid item xs={6} sm={3} onClick={() => navigate("/drivers")} sx={{ cursor: 'pointer' }}>
              <Stack alignItems="center" sx={{ py: 1.5 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: "primary.main" }}>
                  {drivers.length || "50+"}
                </Typography>
                <Typography variant="body2" color="text.secondary">Active Drivers</Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3} onClick={() => navigate("/teams")} sx={{ cursor: "pointer" }}>
              <Stack alignItems="center" sx={{ py: 1.5 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: "primary.main" }}>
                  {drivers.map((d) => d.team).filter((t, i, a) => a.indexOf(t) === i).length || "0"}
                </Typography>
                <Typography variant="body2" color="text.secondary">Racing Teams</Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3} onClick={() => navigate("/schedule")}>
              <Stack alignItems="center" sx={{ py: 1.5, cursor: "pointer" }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: "primary.main" }}>
                  {getUpcomingEvents(events, tz, Number.POSITIVE_INFINITY).length}
                </Typography>
                <Typography variant="body2" color="text.secondary">Upcoming Races</Typography>
              </Stack>
            </Grid>
            <Grid item xs={6} sm={3} onClick={() => navigate("/seasons")}>
              <Stack alignItems="center" sx={{ py: 1.5, cursor: "pointer" }}>
                <Typography variant="h3" sx={{ fontWeight: 900, color: "primary.main" }}>
                  {seasons.length || "5"}
                </Typography>
                <Typography variant="body2" color="text.secondary">Seasons</Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        {/* Featured Section with Side Image */}
        <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center" sx={{ mb: { xs: 6, md: 10 } }}>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="a.png"
              alt="Racing"
              sx={{
                width: "100%",
                height: { xs: 260, sm: 380, md: 460 },
                objectFit: "cover",
                borderRadius: 2,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2} sx={{ height: "100%", justifyContent: "center" }}>
              <Typography variant="overline" color="primary.main">Built for close racing</Typography>
              <Typography variant="h4" sx={{ fontSize: { xs: "2.1rem", md: "3.25rem" } }}>
                Premium Sim Racing Experience
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Experience the thrill of competitive sim racing with our league system and experience intense wheel-to-wheel action.
              </Typography>
              <Box sx={{ pt: 1 }}>
                <Button fullWidth={false} variant="contained" color="primary" component={RouterLink} to="/rules" sx={{ width: { xs: "100%", sm: "auto" } }}>
                  Learn Our Standards
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        {/* Upcoming events with visual enhancement */}
        {upcomingEvents.length > 0 && (
          <Paper
            sx={{
              p: { xs: 1.5, sm: 3 },
              mb: { xs: 5, md: 8 },
              borderRadius: 2,
              backgroundImage: "linear-gradient(135deg, rgba(31,204,220,0.05) 0%, rgba(247,56,59,0.05) 100%)",
            }}
            onClick={() => navigate("/schedule")}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Upcoming Events</Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Game</TableCell>
                  <TableCell>Season</TableCell>
                  <TableCell>Rnd</TableCell>
                  <TableCell>Track</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Practice</TableCell>
                  <TableCell>Qualifying</TableCell>
                  <TableCell>Race</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upcomingEvents.map((e, i) => (
                  <TableRow key={`${e.date}-${e.game}-${e.round ?? i}`}>
                    <TableCell>{e.game}</TableCell>
                    <TableCell>{e.season}</TableCell>
                    <TableCell>{e.round}</TableCell>
                    <TableCell>{e.track}</TableCell>
                    <TableCell>{formatCalendarDate(e.date, locale)}</TableCell>
                    <TableCell>{e.type}</TableCell>
                    <TableCell>{e.practice || "-"}</TableCell>
                    <TableCell>{e.quali || "-"}</TableCell>
                    <TableCell>{e.race || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Info cards with images */}
        {cards?.length > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {cards.map((card) => (
              <Grid key={card.title} item xs={12} sm={6} md={4}>
                <InfoCard {...card} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Season highlights with images */}
        {seasons.length > 0 && (
          <Box sx={{ mb: 4 }} onClick={() => navigate("/seasons")}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Season Highlights</Typography>
            <Grid container spacing={3}>
              {seasons.map((s, i) => (
                <Grid key={s.name} item xs={12} sm={6} md={4} >
                  <Card sx={{ height: "100%", overflow: "hidden", borderRadius: 3 }}>
                    <CardMedia component="img" height="140" image={s.image} alt={s.name} />
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{s.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Year: {s.year} • Class: {Array.isArray(s.classes) ? s.classes.join(", ") : s.classes}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Rounds: {getRounds(s)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Top drivers with enhanced visuals */}
        {Array.isArray(standingsACC.drivers) && standingsACC.drivers.length > 0 && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, border: "2px solid", borderColor: "primary.main" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Assetto Corsa Competizione Championship Leaders</Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Car</TableCell>
                  <TableCell align="right">Wins</TableCell>
                  <TableCell align="right">Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drivers.sort((a, b) => standingsACC.drivers.find((dd) => dd.id === b.id)?.current_points - standingsACC.drivers.find((dd) => dd.id === a.id)?.current_points).slice(0, 5).map((d, i) => (
                  <TableRow key={d.name + "-" + (d.number || d.position || i)} onClick={() => navigate(`/driver/${d.id}`)} sx={{ cursor: 'pointer' }}>
                    <TableCell>
                      <Avatar src={d.image} alt={`${d.name} profile`} sx={{ width: 40, height: 40, mb: 1.5 }} />
                    </TableCell>
                    <TableCell>{d.position ?? d.number ?? "-"}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.team}</TableCell>
                    <TableCell>{d.car}</TableCell>
                    <TableCell align="right">{d.wins ?? 0}</TableCell>
                    <TableCell align="right">
                      <Chip label={standingsACC.drivers.find((dd) => dd.id === d.id)?.current_points} color={i === 0 ? "warning" : "default"} size="small" />
                      {/**/}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </Paper>
        )}

        {aceLeaders.length > 0 && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, border: "2px solid", borderColor: "primary.main" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              {messages.assettoCorsaEvoChampionshipLeaders}
            </Typography>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    <TableCell>#</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Team</TableCell>
                    <TableCell>Car</TableCell>
                    <TableCell align="right">Wins</TableCell>
                    <TableCell align="right">Points</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aceLeaders.map((driver, index) => (
                    <TableRow
                      key={driver.id || `${driver.name}-${index}`}
                      onClick={() => driver.id && navigate(`/driver/${driver.id}`)}
                      sx={{ cursor: driver.id ? "pointer" : "default" }}
                    >
                      <TableCell>
                        <Avatar
                          src={driver.image}
                          alt={`${driver.name} profile`}
                          sx={{ width: 40, height: 40, mb: 1.5 }}
                        />
                      </TableCell>
                      <TableCell>{driver.position ?? driver.number ?? "-"}</TableCell>
                      <TableCell>{driver.name}</TableCell>
                      <TableCell>{driver.team}</TableCell>
                      <TableCell>{driver.car}</TableCell>
                      <TableCell align="right">{driver.current_wins ?? driver.wins ?? 0}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={driver.current_points ?? driver.points ?? 0}
                          color={index === 0 ? "warning" : "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Top drivers with enhanced visuals */}
        {/* {standingsAC.drivers.length > 0 && (
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3, border: "2px solid", borderColor: "primary.main" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>Assetto Corsa Championship Leaders</Typography>
            <Table sx={{ overflowX: "auto", width: "100%", margin: "auto", display: "block" }}>
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Car</TableCell>
                  <TableCell align="right">Wins</TableCell>
                  <TableCell align="right">Points</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {drivers.sort((a, b) => standingsAC.drivers.find((dd) => dd.id === b.id).points - standingsAC.drivers.find((dd) => dd.id === a.id).points).slice(0, 5).map((d, i) => (
                  <TableRow key={d.name + "-" + (d.number || d.position || i)} onClick={() => navigate(`/driver/${d.id}`)} sx={{cursor: 'pointer'}}>
                    <TableCell>
                      <Avatar src={d.image} alt={`${d.name} profile`} sx={{ width: 40, height: 40, mb: 1.5 }} />
                    </TableCell>
                    <TableCell>{d.position ?? d.number ?? "-"}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.team}</TableCell>
                    <TableCell>{d.car}</TableCell>
                    <TableCell align="right">{d.wins ?? 0}</TableCell>
                    <TableCell align="right">
                      <Chip label={standingsAC.drivers.find((dd) => dd.id === d.id)?.points} color={i === 0 ? "warning" : "default"} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )} */}

        {/* Join CTA with background */}
        <Box
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 2,
            position: "relative",
            overflow: "hidden",
            backgroundImage: "url(2.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              insetInline: 0,
              bottom: 0,
              background: "linear-gradient(90deg, rgba(0,12,32,0.9) 0%, rgba(0,12,32,0.7) 100%)",
            },
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>Ready to race?</Typography>
              <Typography variant="body1" color="text.secondary">Join the most competitive sim racing league in South Africa</Typography>
            </Box>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", md: "auto" } }}>
              <Button fullWidth variant="contained" color="secondary" component={RouterLink} to="/join">
                Register Now
              </Button>
              <Button fullWidth variant="outlined" color="primary" href="/join" target="_blank">
                Join Discord
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Sponsors with visual enhancement */}
        {sponsors.length > 0 && (
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="overline" sx={{ letterSpacing: 1, opacity: 0.7 }}>Our Partners</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="center" sx={{ mt: 2 }}>
              {sponsors.map((s, i) => (
                <Chip
                  key={s.name + "-" + i}
                  label={s.name}
                  component="a"
                  href={s.url}
                  target="_blank"
                  clickable
                  variant="outlined"
                  sx={{
                    borderColor: "primary.main",
                    "&:hover": { backgroundColor: "primary.main", color: "background.default" },
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
      </Container>
    </>
  );
}
